import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import config from '@plone/volto/registry';
import { toGettextLang } from '@plone/volto/helpers/Utils/Utils';
import { getFallbackLayout } from '../actions/fallback';
import { getLangFromPath } from '@fhnw/helpers/getLangFromPath';
import { changeLanguage } from '@plone/volto/actions/language/language';

/**
 * A custom React hook to dispatch an action that fetches essential layout data
 * (navigation, footer, etc.) for error pages.
 *
 * It ensures the action is dispatched only once per page load, even if the
 * component re-renders. It also makes sure the language is set correctly based on the URL.
 */
export function useFallbackLayout(): void {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const currentLang = useSelector((state: any) => state.intl.locale);
  const fallbackLayoutLoaded = useSelector(
    (state: any) => state.fallbackLayout.loaded,
  );
  const hasDispatched = useRef(false);

  const langFromUrl =
    getLangFromPath(location.pathname) ||
    (config.settings.defaultLanguage as string);

  useEffect(() => {
    // Set correct language for the error page if it differs from current state
    if (langFromUrl && langFromUrl !== currentLang) {
      const langFileName = toGettextLang(langFromUrl);
      import(`@root/../locales/${langFileName}.json`).then((locale) => {
        dispatch(changeLanguage(langFromUrl, locale.default, undefined));
      });
    }

    // Dispatch the action to fetch layout data only once.
    if (!fallbackLayoutLoaded && !hasDispatched.current) {
      hasDispatched.current = true;
      const langRoot = config.settings.isMultilingual ? `/${langFromUrl}` : '/';
      dispatch(getFallbackLayout(langRoot));
    }
    // Re-run effect if the language in the URL changes, or if the current language state changes.
  }, [dispatch, langFromUrl, currentLang, fallbackLayoutLoaded]);
}
